import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { IWarehouse } from '../../../shared/interfaces/location-warehouse.interface';

import { LocationApiService } from './../../../shared/services/api/location/location-api.service';

@Component({
  selector: 'lum-ui-location-confirm-dialog',
  templateUrl: './ui-location-confirm-dialog.component.html',
  styleUrls: ['./ui-location-confirm-dialog.component.scss'],
})
export class UiLocationConfirmDialog implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  lineItems :UntypedFormGroup[] = [];
  private onDestroy$ = new Subject();
  warehouses :IWarehouse[] = [];
  locations :{ [key :number] :IWarehouse[] } = {};

  constructor(
    public dialogRef :MatDialogRef<UiLocationConfirmDialog>,
    @Inject(MAT_DIALOG_DATA)
    public data :{
      title :string;
      message :string;
      module :string;
      location :any;
    },
    private fb :UntypedFormBuilder,
    private locationApiService :LocationApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initForm();
  }

  loadLocationsByWarehouseId(warehouseId :number = 0, index :number = -1) :void {
    const lineItems = this.form?.get('lineItems') as UntypedFormArray;
    if (warehouseId <= 0) {
      return;
    }

    if (this.locations[warehouseId]) {
      const locationId = this.locations[warehouseId][0]?.id ?? null;
      if (locationId > 0) {
        if (index < 0) {
          lineItems?.controls.forEach((lineItem :any) => {
            lineItem?.patchValue({
              locationId: locationId,
            });
          });
        }

        if (index >= 0) {
          const lineItem = lineItems?.get(index.toString()) ?? null;
          lineItem?.patchValue({
            locationId: locationId,
          });
        }
      }

      return;
    }

    this.locationApiService
      .getLocationsByParentId({
        all: '1',
        parent_id: warehouseId.toString(),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response?.length > 0) {
            const locations = response.filter(
              (location :any) => location.id !== this.data.location.id,
            );
            this.locations[warehouseId] = locations;

            const locationId = this.locations[warehouseId][0]?.id ?? null;
            if (locationId > 0) {
              if (index < 0) {
                lineItems?.controls.forEach((lineItem :any) => {
                  lineItem?.patchValue({
                    locationId: locationId,
                  });
                });
              }

              if (index >= 0) {
                const lineItem = lineItems?.get(index.toString()) ?? null;
                lineItem?.patchValue({
                  locationId: locationId,
                });
              }
            }
          }
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSelectWarehouseEvent(event :MatSelectChange, index :number) :void {
    this.loadLocationsByWarehouseId(event.value, index);
  }

  onConfirmClick() :void {
    if (this.form.value.inputValue === this.data.location.location_name) {
      this.dialogRef.close(this.form.value);
    } else {
      this.dialogRef.close(null);
    }
  }

  private nameValidator(name :string) :ValidatorFn {
    return (control :AbstractControl) :{ [key :string] :any } | null => {
      const inputtedName = control.value;

      return inputtedName === name
        ? null
        : { nameNotFound: { value: inputtedName } };
    };
  }

  private initForm() :void {
    this.lineItems = [];
    this.data.location.rfqstock_locations.forEach((stockLocation :any) => {
      this.lineItems.push(
        this.fb.group({
          rfqId: new UntypedFormControl(stockLocation.rfq_stock.rfq.id ?? null, [
            Validators.required,
          ]),
          warehouseId: new UntypedFormControl(
            this.data.location.parent_id ?? null,
            [Validators.required],
          ),
          locationId: new UntypedFormControl(null, [Validators.required]),
          internalSku: new UntypedFormControl(
            stockLocation.rfq_stock.rfq.internal_sku ?? null,
          ),
          lotId: new UntypedFormControl(stockLocation.lot_id ?? null),
        }),
      );
    });

    this.form = this.fb.group({
      inputValue: new UntypedFormControl('', [
        Validators.required,
        this.nameValidator(this.data.location.location_name),
      ]),
      lineItems: this.fb.array(this.lineItems),
    });

    const warehouseGroups =
      this.data?.location?.parent?.warehouse_groups[0] ?? null;
    this.warehouses = warehouseGroups?.warehouses ?? [];
    this.loadLocationsByWarehouseId(this.data.location.parent_id);
  }
}
